import { ColorVariants } from '@helpers/Constants';
import { ModuleActionTag } from '@helpers/Models';

/**
 * Converts HTML content to plain text with custom [tag] syntax restored
 * @param htmlContent - The raw HTML string to clean
 * @returns Cleaned plain text with [tag]s
 */
export const contentConversion = (htmlContent: string): string => {
  const temp = document.createElement('div');
  temp.innerHTML = htmlContent;

  temp.querySelectorAll('span[data-tag]').forEach((tagElement) => {
    const tagText = tagElement.getAttribute('data-tag');
    if (tagText) {
      const textNode = document.createTextNode(tagText);
      tagElement.replaceWith(textNode);
    }
  });

  return temp.textContent?.trim() || '';
};

/**
 * Generates a consistent color scheme for a given tag based on its hash.
 *
 * This function:
 * - Computes a hash from the tag string using the sum of its character codes.
 * - Maps that hash to a color key from the available `ColorVariants.light` keys.
 * - Returns a set of colors (background, border, and text) using the light and dark color variants.
 *
 * This ensures that each tag gets a visually distinct and repeatable color scheme.
 *
 * @param {string} tag - The tag string used to derive a unique color combination.
 * @returns {{
 *   backgroundColor: string;
 *   borderColor: string;
 *   textColor: string;
 * }} An object containing CSS-compatible color values for styling the tag.
 */
export const getColors = (tag: string) => {
  // Get the list of light variant color keys
  const lightVariant = Object.keys(
    ColorVariants.light,
  ) as (keyof typeof ColorVariants.light)[];

  // Generate a simple hash by summing character codes in the tag
  const hash = Array.from(tag).reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0,
  );

  // Use the hash to pick a color key deterministically
  const index = hash % lightVariant.length;
  const colorKey = lightVariant[index];

  // Return the generated color values
  return {
    backgroundColor: ColorVariants.light[colorKey],
    borderColor: ColorVariants.dark[colorKey],
    textColor: ColorVariants.dark[colorKey],
  };
};

/**
 * Inserts a styled, non-editable tag element into a content-editable HTML container (typically a rich text editor).
 *
 * @param {HTMLElement} editor - The content-editable container where the tag will be inserted.
 * @param {string} tag - The tag label/text to insert.
 * @param {() => void} [onDelete] - Optional callback invoked when the tag's close button is clicked.
 * @param {boolean} [includeBrackets=false] - If `true`, wraps the tag text in square brackets (e.g., [tag]).
 * @param {() => void} [onInsert] - Optional callback triggered after the tag is inserted, unless `skipRefresh` is `true`.
 * @param {boolean} [skipRefresh=false] - If `true`, skips calling the `onInsert` callback after insertion.
 *
 * Function behavior:
 * - Creates a `<span>` element styled as a pill/tag (non-editable).
 * - Appends a clickable `×` button to remove the tag from the editor.
 * - Optionally calls `onDelete` when the tag is removed and `onInsert` when the tag is inserted.
 * - Adds a non-breaking space (`&nbsp;`) after the tag for visual spacing.
 */
export const insertTag = (
  editor: HTMLElement,
  tag: string,
  onDelete?: () => void,
  includeBrackets = false,
  onInsert?: () => void,
  skipRefresh = false,
): void => {
  const colors = getColors(tag);

  // Create the tag element
  const span = document.createElement('span');
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('data-tag', tag);
  span.style.display = 'inline-flex';
  span.style.alignItems = 'center';
  span.style.padding = '2px 6px';
  span.style.margin = '2px';
  span.style.borderRadius = '9999px';
  span.style.fontSize = '0.875rem';
  span.style.backgroundColor = colors.backgroundColor;
  span.style.border = `1px solid ${colors.borderColor}`;
  span.style.color = colors.textColor;

  span.textContent = includeBrackets ? `[${tag}]` : tag;

  // For Creating and configuring the close (×) button
  const closeBtn = document.createElement('span');
  closeBtn.innerHTML = '×';
  closeBtn.style.marginLeft = '6px';
  closeBtn.style.cursor = 'pointer';
  closeBtn.onclick = () => {
    span.remove(); // Remove the tag from the editor
    if (onDelete) onDelete(); // Call delete callback if provided
  };

  // Add close button to tag
  span.appendChild(closeBtn);

  editor.appendChild(span);
  editor.appendChild(document.createTextNode('\u00A0')); // Adding space after tag
  // Optionally call the insert callback (unless skipped)
  if (!skipRefresh) {
    onInsert?.();
  }
};

/**
 * Returns the list of available tags that have not yet been inserted into the HTML container.
 *
 * This function checks the DOM for existing tag elements (`<span data-tag="...">`) and filters
 * out those tags from the provided `tags` list.
 *
 * @param {HTMLElement} htmlContainer - The container element (usually the editor) to scan for existing tags.
 * @param {ModuleActionTag[]} tags - The full list of possible tags (e.g., from a module action).
 * @returns {ModuleActionTag[]} A filtered list of tags that are not currently present in the container.
 */
export const getAvailableTagsFromList = (
  htmlContainer: HTMLElement,
  tags: ModuleActionTag[],
): ModuleActionTag[] => {
  // Extract all tag values which is prepopulated
  const existingTags = Array.from(
    htmlContainer.querySelectorAll('span[data-tag]'),
  )
    .map((element) => element.getAttribute('data-tag'))
    .filter((tag): tag is string => !!tag); // Filter out null values

  // Return only the tags that haven't been added yet
  return tags.filter((tag) => !existingTags.includes(tag.tag));
};

/**
 * Filters out actions from a selected module that have already been completed.
 *
 * This function compares the `ModuleActions` of a `selectedModule` against a list of `modules`
 * that contain already completed `moduleActions`, and returns only the actions that are still available.
 *
 * @param {{ id: number; ModuleActions: { id: number; name: string }[] }} selectedModule
 *   The module currently selected by the user, containing its available actions.
 *
 * @param {{
 *   moduleId: number;
 *   moduleActions: { moduleActionId: number }[];
 * }[]} modules
 *   A list of modules with their completed or used actions (typically representing what's already assigned).
 *
 * @returns {{ id: number; name: string }[]}
 *   A filtered list of actions from the selected module that are not yet completed or assigned.
 */
export const getFilteredActions = (
  selectedModule: { id: number; ModuleActions: { id: number; name: string }[] },
  modules: {
    moduleId: number;
    moduleActions: { moduleActionId: number }[];
  }[],
): { id: number; name: string }[] => {
  if (!selectedModule) return [];

  return selectedModule.ModuleActions.filter((action) => {
    // Find the module in the completed list that matches the selected module
    const matchedModule = modules.find(
      (mod) => mod.moduleId === selectedModule.id,
    );

    // If no matching module is found in the completed list, all actions are available
    if (!matchedModule) return true;

    // Return true if this action has not been added yet
    return !matchedModule.moduleActions.some(
      (completedAction) => completedAction.moduleActionId === action.id,
    );
  });
};
